import type { DowngradeImpactPreviewDto } from '@/features/billing/types';

/** Suppressions et pertes d'usage que le back appliquera à la bascule, comptées sur le compte réel. */
export type DowngradeImpactCount = {
  key: 'teamMembers' | 'liveWebinars' | 'evergreenSources';
  count: number;
};

/** Plafonds du forfait cible, listés en entier : le client doit voir ce que devient son forfait. */
export type DowngradeTargetLimit =
  | { key: 'participants' | 'sessionDuration' | 'intervenants' | 'teamMembers'; value: number }
  | { key: 'youtube'; allowed: boolean };

export type DowngradeSummary = {
  impacts: DowngradeImpactCount[];
  limits: DowngradeTargetLimit[];
  hasImpacts: boolean;
};

function countedImpacts(preview: DowngradeImpactPreviewDto): DowngradeImpactCount[] {
  return (
    [
      { key: 'teamMembers', count: preview.teamMembersToRemoveCount },
      { key: 'liveWebinars', count: preview.liveWebinarsWithIntervenantsToReduceCount },
      { key: 'evergreenSources', count: preview.finishedLiveSessionsIneligibleForEvergreenCount },
    ] as const
  )
    .filter((impact) => impact.count > 0)
    .map((impact) => ({ ...impact }));
}

function targetLimits(preview: DowngradeImpactPreviewDto): DowngradeTargetLimit[] {
  const limits = preview.targetPlanLimits;
  return [
    { key: 'participants', value: limits.maxParticipantsCount },
    { key: 'sessionDuration', value: limits.maxSessionDurationMinutes },
    { key: 'intervenants', value: limits.maxPresentersCount },
    { key: 'teamMembers', value: limits.maxTeamMembersCount },
    { key: 'youtube', allowed: limits.youtubeTransmissionAllowed },
  ];
}

export function buildDowngradeSummary(
  preview: DowngradeImpactPreviewDto | null | undefined,
): DowngradeSummary {
  if (!preview) {
    return { impacts: [], limits: [], hasImpacts: false };
  }
  const impacts = countedImpacts(preview);
  return { impacts, limits: targetLimits(preview), hasImpacts: impacts.length > 0 };
}

/** Le back raisonne en minutes, le récapitulatif s'adresse à un client : 240 devient « 4h ». */
export function formatDurationLimit(minutes: number): { hours: number; minutes: number } {
  const safeMinutes = Number.isFinite(minutes) && minutes > 0 ? Math.floor(minutes) : 0;
  return { hours: Math.floor(safeMinutes / 60), minutes: safeMinutes % 60 };
}
